import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import type { IRouteInfoStop } from '../types.js';

@Entity()
export class RouteInfo extends AdornisEntity {
  static override _class = 'RouteInfo';

  @Field(_type => String, { default: v => v ?? A.getGloballyUniqueID() }) _id?: string;
  @Field(_type => String) vehicleId?: string;
  @Field(_type => String) vehicleName?: string;
  @Field(_type => String) color?: string;
  @Field(_type => [RouteInfoStop]) stops?: RouteInfoStop[];

  constructor(doc: PartialEntityData<RouteInfo>) {
    super(doc);
  }

  override toString(): string {
    const lines: string[] = [];
    lines.push(`\nRoute: ${this.vehicleId || 'N/A'} (${this._id})`);
    lines.push('─'.repeat(130));
    lines.push(
      `${'#'.padEnd(4)} ${'Typ'.padEnd(10)} ${'Adresse'.padEnd(35)} ${'Koordinaten'.padEnd(25)} ${'Start'.padEnd(
        8,
      )} ${'Fahrt'.padEnd(10)} ${'Service'.padEnd(10)}`,
    );
    lines.push('─'.repeat(130));

    let totalTravelMinutes = 0;
    let totalServiceMinutes = 0;

    if (this.stops && this.stops.length > 0) {
      this.stops.forEach((stop, index) => {
        const num = `${index + 1}.`.padEnd(4);
        const type = (stop.type || 'N/A').padEnd(10);
        const address = (stop.address || 'Keine Adresse').substring(0, 32).padEnd(35);
        const coords = `${stop.lat?.toFixed(4) || 'N/A'}, ${stop.lng?.toFixed(4) || 'N/A'}`.padEnd(25);
        const startTimeStr = stop.startTime
          ? new Date(stop.startTime).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
          : 'N/A';
        const travelDuration =
          stop.travelDurationMinutes !== undefined && stop.travelDurationMinutes !== null
            ? `${Math.round(stop.travelDurationMinutes)} min`
            : 'N/A';
        const serviceDuration =
          stop.serviceDurationMinutes !== undefined && stop.serviceDurationMinutes !== null
            ? `${stop.serviceDurationMinutes.toFixed(1).replace('.0', '')} min`
            : 'N/A';

        // Summiere Zeiten
        if (stop.travelDurationMinutes !== undefined && stop.travelDurationMinutes !== null)
          totalTravelMinutes += stop.travelDurationMinutes;
        if (stop.serviceDurationMinutes !== undefined && stop.serviceDurationMinutes !== null)
          totalServiceMinutes += stop.serviceDurationMinutes;

        lines.push(
          `${num} ${type} ${address} ${coords} ${startTimeStr.padEnd(8)} ${travelDuration.padEnd(
            10,
          )} ${serviceDuration.padEnd(10)}`,
        );
      });
    } else {
      lines.push('Keine Stops vorhanden');
    }

    // Berechne Gesamtzeit basierend auf Start- und Endzeit (Elapsed Time)
    let totalElapsedMinutes = totalTravelMinutes + totalServiceMinutes;
    if (this.stops && this.stops.length >= 2) {
      const firstStop = this.stops[0];
      const lastStop = this.stops[this.stops.length - 1];
      if (firstStop?.startTime && lastStop?.startTime) {
        const startTime = new Date(firstStop.startTime);
        const endTime = new Date(lastStop.startTime);
        totalElapsedMinutes = (endTime.getTime() - startTime.getTime()) / 1000 / 60;
      }
    }

    const totalHours = Math.floor(totalElapsedMinutes / 60);
    const remainingMinutes = Math.round(totalElapsedMinutes % 60);

    lines.push('─'.repeat(130));
    lines.push(
      `Total: ${this.stops?.length || 0} Stops | Fahrtzeit: ${Math.round(
        totalTravelMinutes,
      )} min | Service: ${Math.round(totalServiceMinutes)} min | Gesamt: ${totalHours}h ${remainingMinutes}min\n`,
    );
    return lines.join('\n');
  }
}

@Entity()
export class RouteInfoStop extends AdornisEntity implements IRouteInfoStop {
  static override _class = 'RouteInfoStop';

  @Field(_type => String, { default: v => v ?? A.getGloballyUniqueID() }) _id?: string;
  @Field(_type => Float) lng: Maybe<number>;
  @Field(_type => Float) lat: Maybe<number>;
  @Field(_type => String) type: Maybe<string>;
  @Field(_type => String) ticketId: Maybe<string>; // Ticket-ID (nur bei type='ticket')
  @Field(_type => String) ticketType: Maybe<string>; // Ticket-Type (Client, Enterprise, HES, IMAC)
  @Field(_type => String) address: Maybe<string>;
  @Field(_type => String) startTime: Maybe<string>; // ISO timestamp
  @Field(_type => Float) travelDurationMinutes: Maybe<number>; // Fahrtzeit zum Stop
  @Field(_type => Float) travelMeters: Maybe<number>; // Meter zum Stop
  @Field(_type => Float) serviceDurationMinutes: Maybe<number>; // Bearbeitungszeit am Stop

  constructor(doc: PartialEntityData<RouteInfoStop>) {
    super(doc);
  }
}
